<script setup lang="ts">
import { computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import ArrowLeft from 'lucide-vue-next/dist/esm/icons/arrow-left.js'; import ChevronRight from 'lucide-vue-next/dist/esm/icons/chevron-right.js'; import ExternalLink from 'lucide-vue-next/dist/esm/icons/external-link.js'; import MessageCircle from 'lucide-vue-next/dist/esm/icons/message-circle.js';





import {
  NxAvatar,
  NxBanner,
  NxChatComposer,
  NxChatMessage,
  NxChatThread,
  NxEmptyState,
  NxFloatingPanel,
  NxIconButton,
  NxList,
  NxListRow,
  NxSearchInput,
  NxSpinner,
} from '@blpde/nx-ui';
import { useNxSocket } from '@blpde/connect';
import { useShellChat } from '@/chat/useShellChat';

const { t } = useI18n();
const router = useRouter();
const {
  open,
  view,
  booting,
  loadingThread,
  sending,
  error,
  filteredAccounts,
  partner,
  channel,
  messages,
  draft,
  search,
  stickKey,
  myAccountId,
  closeChat,
  selectPartner,
  backToPicker,
  send,
  handleLiveEvent,
} = useShellChat();

// What the other person writes arrives live: the chat module's frames on the
// shell socket. Without a shared socket the panel shows what it loaded.
const stopLive = useNxSocket()?.channel('chat').on('data', handleLiveEvent);
onBeforeUnmount(() => stopLive?.());

/** Clear of the assistant button in the corner. */
const PANEL_MARGIN = { x: 20, y: 92 };
const PANEL_WIDTH = 'var(--nxt-panel-width-2xl)';
const PANEL_HEIGHT = 'min(calc(var(--nxt-size-unit) * 35), calc(100vh - var(--nxt-area-size-md)))';

const title = computed(() => {
  if (view.value === 'thread' && partner.value) {
    return partner.value.name || partner.value.email || t('connect.chat.title');
  }
  return t('connect.chat.title');
});

const subtitle = computed(() => {
  if (view.value === 'thread' && partner.value?.email && partner.value.name) {
    return partner.value.email;
  }
  return t('connect.chat.subtitle');
});

function isMine(msg: { senderAccountId?: number; sender_account_id?: number }) {
  const sid = msg.senderAccountId ?? msg.sender_account_id;
  return myAccountId.value != null && sid === myAccountId.value;
}

/** The panel's own close button and Escape end the session like the app's button does. */
function onOpenChange(isOpen: boolean) {
  if (!isOpen) closeChat();
}

function onSend(content: string) {
  void send(content);
}

function openFullApp() {
  const id = channel.value?.id;
  closeChat();
  if (id) {
    void router.push({ path: `/chat`, query: { channel: String(id) } });
    return;
  }
  void router.push('/chat');
}
</script>

<template>
  <NxFloatingPanel
    :open="open"
    :title="title"
    :subtitle="subtitle"
    position-key="connect.chat"
    :margin="PANEL_MARGIN"
    :width="PANEL_WIDTH"
    :height="PANEL_HEIGHT"
    :close-label="t('connect.chat.close')"
    :close-on-escape="false"
    flush
    @update:open="onOpenChange"
  >
    <template #header-leading>
      <NxIconButton
        v-if="view === 'thread'"
        height="xs"
        :title="t('connect.chat.back')"
        :aria-label="t('connect.chat.back')"
        @click="backToPicker"
      >
        <ArrowLeft class="nxt-icon-md" />
      </NxIconButton>
      <span v-else class="cx-chat__spark" aria-hidden="true">
        <MessageCircle class="nxt-icon-md" />
      </span>
    </template>
    <template #header-actions>
      <NxIconButton
        height="xs"
        :title="t('connect.chat.openFull')"
        :aria-label="t('connect.chat.openFull')"
        @click="openFullApp"
      >
        <ExternalLink class="nxt-icon-md" />
      </NxIconButton>
    </template>

    <!-- Picker -->
    <template v-if="view === 'picker'">
      <div v-if="booting" class="cx-chat__state">
        <NxSpinner size="lg" />
        <span>{{ t('connect.chat.loading') }}</span>
      </div>
      <NxBanner v-else-if="error" variant="danger" class="cx-chat__banner">{{ error }}</NxBanner>
      <template v-else>
        <div class="cx-chat__search">
          <NxSearchInput v-model="search" :placeholder="t('connect.chat.searchPlaceholder')" />
        </div>
        <div class="cx-chat__list">
          <NxList>
            <NxListRow
              v-for="acc in filteredAccounts"
              :key="acc.id"
              :title="acc.name || acc.email"
              :subtitle="acc.email && acc.name ? acc.email : undefined"
              @open="selectPartner(acc)"
            >
              <template #leading>
                <NxAvatar :name="acc.name || acc.email || '?'" :size="36" shape="rounded" decorative />
              </template>
              <template #trailing>
                <ChevronRight class="nxt-icon-md" aria-hidden="true" />
              </template>
            </NxListRow>
          </NxList>
          <NxEmptyState v-if="filteredAccounts.length === 0" compact :title="t('connect.chat.noResults')" />
        </div>
      </template>
    </template>

    <!-- Thread -->
    <template v-else>
      <div v-if="loadingThread" class="cx-chat__state">
        <NxSpinner size="lg" />
        <span>{{ t('connect.chat.loadingThread') }}</span>
      </div>
      <template v-else>
        <NxBanner v-if="error" variant="danger" class="cx-chat__banner">{{ error }}</NxBanner>
        <NxChatThread
          class="cx-chat__thread"
          :stick-key="stickKey"
          :near-bottom-px="64"
        >
          <NxEmptyState v-if="messages.length === 0" compact :title="t('connect.chat.empty')" />
          <NxChatMessage
            v-for="m in messages"
            :key="m.id"
            :tone="isMine(m) ? 'user' : 'agent'"
            compact
          >
            <template #meta>
              <span>
                {{
                  isMine(m)
                    ? t('connect.chat.you')
                    : m.senderName || partner?.name || t('connect.chat.them')
                }}
              </span>
            </template>
            <div class="cx-chat__bubble">{{ m.content }}</div>
          </NxChatMessage>
        </NxChatThread>
        <NxChatComposer
          v-model="draft"
          class="cx-chat__composer"
          :placeholder="t('connect.chat.placeholder')"
          :sending="sending"
          :disabled="!channel || sending"
          @send="onSend"
        />
      </template>
    </template>
  </NxFloatingPanel>
</template>

<style scoped>
.cx-chat__spark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--nxt-tile-size-sm);
  height: var(--nxt-tile-size-sm);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
  flex-shrink: 0;
}

.cx-chat__search {
  margin: var(--nxt-space-sm) var(--nxt-space-sm) var(--nxt-space-xs);
  flex-shrink: 0;
}

.cx-chat__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 0 var(--nxt-space-xs) calc(var(--nxt-space-xs) * 1.25);
}

.cx-chat__thread {
  flex: 1;
  min-height: 0;
  padding: calc(var(--nxt-space-xs) * 1.25) var(--nxt-space-sm);
}

.cx-chat__bubble {
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  white-space: pre-wrap;
  word-break: break-word;
}

.cx-chat__composer {
  flex-shrink: 0;
}

.cx-chat__state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: calc(var(--nxt-space-xs) * 1.25);
  padding: var(--nxt-space-lg);
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-muted);
  text-align: center;
}
.cx-chat__banner {
  flex-shrink: 0;
  margin: var(--nxt-space-xs) var(--nxt-space-sm) 0;
}
</style>
